import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaDownload, FaEnvelope, FaPrint } from 'react-icons/fa';
import DashboardLayout from '../../components/DashboardLayout';
import companyLogo from '../../assets/images/login-logo.png';
import './PaySlipView.css';
import { API_BASE_URL, clearAuthSession } from '../../utils/api';
import { getActiveCompanyId } from '../../utils/companyContext';
import { resolveFileUrl } from '../../utils/fileUrl';
import { formatEmployeeId } from '../../utils/employeeId';
import { getModulePermissions } from '../../utils/permissions';
const resolveCompanyLogoUrl = resolveFileUrl;

const sanitizeFileSegment = (value, fallback) => {
  if (!value) {
    return fallback;
  }

  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

  return normalized || fallback;
};

const getErrorMessageFromResponse = async (response, fallback) => {
  try {
    const text = await response.text();
    if (!text) {
      return fallback;
    }

    try {
      const parsed = JSON.parse(text);
      return parsed?.message || parsed?.error || fallback;
    } catch (_error) {
      return text;
    }
  } catch (_error) {
    return fallback;
  }
};

const convertScientificToPlain = (value) => {
  const match = value.match(/^([+-]?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/);
  if (!match) {
    return value;
  }

  const sign = match[1] === '-' ? '-' : '';
  const integerPart = match[2];
  const fractionalPart = match[3] ?? '';
  const exponent = Number.parseInt(match[4], 10);

  if (!Number.isFinite(exponent)) {
    return value;
  }

  const digits = `${integerPart}${fractionalPart}`;
  const zerosToAppend = exponent - fractionalPart.length;

  if (zerosToAppend >= 0) {
    return `${sign}${digits}${'0'.repeat(zerosToAppend)}`;
  }

  const decimalIndex = digits.length + zerosToAppend;
  if (decimalIndex <= 0) {
    return `${sign}0.${'0'.repeat(Math.abs(decimalIndex))}${digits}`;
  }

  const wholePart = digits.slice(0, decimalIndex) || '0';
  const fraction = digits.slice(decimalIndex);
  return `${sign}${wholePart}.${fraction}`;
};

const formatAccountNumber = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  const str = String(value).trim();
  if (!str) {
    return null;
  }

  const decimalMatch = str.match(/^([+-]?)(\d+)\.(\d+)$/);
  if (decimalMatch && /^0+$/.test(decimalMatch[3])) {
    const sign = decimalMatch[1] === '-' ? '-' : '';
    return `${sign}${decimalMatch[2]}`;
  }

  if (!/[eE]/.test(str)) {
    return str;
  }

  return convertScientificToPlain(str);
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${day}-${month}-${year}`;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString('en-GB');
};

const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }

  const cleaned = String(value).replace(/,/g, '').trim();
  if (!cleaned) {
    return null;
  }

  const parsed = Number(cleaned);
  if (Number.isNaN(parsed)) {
    return null;
  }

  return parsed;
};

const formatAmount = (value) => {
  const parsed = parseAmount(value);
  if (parsed === null) {
    return '-';
  }

  return parsed.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const numberToWordsIndian = (num) => {
  num = Math.floor(num);
  if (Number.isNaN(num)) return '';
  if (num === 0) return 'zero';

  const ones = [
    '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen',
    'sixteen', 'seventeen', 'eighteen', 'nineteen',
  ];
  const tens = [
    '', '', 'twenty', 'thirty', 'forty', 'fifty',
    'sixty', 'seventy', 'eighty', 'ninety',
  ];

  const belowThousand = (n) => {
    let str = '';
    if (n > 99) {
      str += ones[Math.floor(n / 100)] + ' hundred';
      n = n % 100;
      if (n) str += ' ';
    }
    if (n > 19) {
      str += tens[Math.floor(n / 10)];
      if (n % 10) str += ' ' + ones[n % 10];
    } else if (n > 0) {
      str += ones[n];
    }
    return str;
  };

  let result = '';

  const crore = Math.floor(num / 10000000);
  if (crore) {
    result += belowThousand(crore) + ' crore';
    num %= 10000000;
    if (num) result += ' ';
  }

  const lakh = Math.floor(num / 100000);
  if (lakh) {
    result += belowThousand(lakh) + ' lakh';
    num %= 100000;
    if (num) result += ' ';
  }

  const thousand = Math.floor(num / 1000);
  if (thousand) {
    result += belowThousand(thousand) + ' thousand';
    num %= 1000;
    if (num) result += ' ';
  }

  const hundredAndBelow = num % 1000;
  if (hundredAndBelow) {
    result += belowThousand(hundredAndBelow);
  }

  return result.trim();
};

const amountToWords = (value) => {
  const n = parseAmount(value);
  if (n === null) return '';
  const rupees = Math.round(n); // ignore paise for text
  if (rupees === 0) return 'zero rupees';
  return `${numberToWordsIndian(rupees)} rupees`;
};

const PaySlipView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const payslipViewPermissions = getModulePermissions('payslip.view');
  const canDownloadPayslipPdf = payslipViewPermissions.export;
  const payslip = location.state?.payslip;
  useEffect(() => {
    if (!payslip) {
      navigate('/payslip/list', { replace: true });
    }
  }, [payslip, navigate]);

  const employee = payslip?.employee ?? null;
  const employeeCompany = employee?.company ?? null;

  const resolvedCompanyName =
    employeeCompany?.name ||
    payslip?.company?.name ||
    payslip?.companyName ||
    'Company Name';
  const resolvedCompanyAddress =
    payslip?.companyAddress ||
    payslip?.company?.address ||
    employeeCompany?.address ||
    payslip?.address ||
    'Company Address';
  const resolvedCompanyEmail =
    payslip?.companyEmail ||
    payslip?.email ||
    payslip?.company?.email ||
    employeeCompany?.email ||
    '';
  const resolvedCompanyPhone =
    payslip?.companyPhone ||
    payslip?.company?.phone ||
    '';
  const logoCandidates = [
    payslip?.companyLogo,
    payslip?.company?.logoUrl,
    employeeCompany?.logoUrl,
    payslip?.logoUrl,
  ];
  const resolvedLogoFromData = logoCandidates
    .map((candidate) => resolveCompanyLogoUrl(candidate))
    .find((candidate) => Boolean(candidate));
  // const resolvedLogo = resolvedLogoFromData ?? companyLogo;
  const resolvedLogo = resolvedLogoFromData ?? companyLogo;


  const earnings = [
    {
      label: 'Basic & DA',
      gross: payslip?.basic_da ?? payslip?.basicDa,
      earned: payslip?.basic_da1 ?? payslip?.basicDa1 ?? payslip?.basic_da ?? payslip?.basicDa,
    },
    {
      label: 'HRA',
      gross: payslip?.hra,
      earned: payslip?.hra1 ?? payslip?.hra,
    },
    {
      label: 'SPL Allowance',
      gross: payslip?.spl,
      earned: payslip?.spl1 ?? payslip?.spl,
    },
    {
      label: 'OT Pay',
      gross: 0,
      earned: payslip?.ot_pay ?? payslip?.otPay ?? 0,
    },
    {
      label: 'Incentive',
      gross: 0,
      earned: payslip?.incentive ?? 0,
    },
    {
      label: 'Excess Deduction',
      gross: 0,
      earned: payslip?.excess_deducted ?? payslip?.excessDeducted ?? 0,
    },
  ];

  const deductions = [
    { label: 'PF', value: payslip?.pf },
    { label: 'ESI', value: payslip?.esi },
    { label: 'Shortage', value: payslip?.shortage },
    { label: 'Loan', value: payslip?.advance },
    { label: 'Uniform', value: payslip?.uniform },
    { label: 'TDS', value: payslip?.tds },
  ];

  const aggregateAmounts = (items, field = 'value') => {
    let sum = 0;
    let hasValue = false;

    items.forEach((item) => {
      const amount = parseAmount(item?.[field]);
      if (amount !== null) {
        sum += amount;
        hasValue = true;
      }
    });

    return hasValue ? sum : null;
  };

  const computedEarningsTotal = aggregateAmounts(earnings, 'earned');
  const computedDeductionsTotal = aggregateAmounts(deductions);

  const totalGrossSummary =
    parseAmount(payslip?.gross) ?? aggregateAmounts(earnings, 'gross');

  const totalEarnings =
    parseAmount(payslip?.gross1) ??
    parseAmount(payslip?.totalEarnings) ??
    computedEarningsTotal;

  const totalDeductions =
    parseAmount(payslip?.totalDeductions) ?? computedDeductionsTotal;

  const resolvedNetPayAmount = (() => {
    const grossBase = totalEarnings ?? totalGrossSummary;
    const deductionsBase = totalDeductions;

    if (grossBase !== null && grossBase !== undefined && deductionsBase !== null && deductionsBase !== undefined) {
      return grossBase - deductionsBase;
    }

    const netPayAmount = parseAmount(payslip?.netPay);
    return netPayAmount !== null ? netPayAmount : null;
  })();

  const accountNumberValue = formatAccountNumber(payslip?.accountNumber);

  const resolveFirstString = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined) {
        continue;
      }
      const text = String(value).trim();
      if (text) {
        return text;
      }
    }
    return null;
  };

  const pfNumberDisplay = (() => {
    const uanNumber = resolveFirstString(
      payslip?.uanNumber,
      employee?.pf?.uanNumber,
      employee?.uanNumber,
    );
    if (uanNumber) {
      return uanNumber;
    }
    const pfNumber = resolveFirstString(
      payslip?.pfNumber,
      employee?.pf?.pfNumber,
      employee?.pfNumber,
    );
    return pfNumber ?? '-';
  })();

  const esiNumberDisplay = resolveFirstString(
    employee?.pf?.esiNumber,
    employee?.esiNumber,
  ) ?? '-';

  const fatherOrHusbandDisplay = resolveFirstString(employee?.fatherName) ?? '-';

  const designationDisplay =
    resolveFirstString(employee?.designation, employee?.lastDesignation, payslip?.designation) ?? '-';

  const branchDisplayValue =
    resolveFirstString(
      payslip?.branchName,
      payslip?.branch,
      payslip?.location,
      payslip?.outlet?.branchName,
    ) ?? '-';

  const phoneNumberDisplay =
    resolveFirstString(
      employee?.primaryMobileNumber,
      employee?.phoneNumber,
      employee?.phone,
      payslip?.phoneNumber,
      payslip?.phone,
    ) ?? '-';

  const employeeIdDisplay = formatEmployeeId(
    resolveFirstString(
      employee?.permanentId,
      employee?.permanentEmployeeId,
      employee?.tempId,
      employee?.tempEmployeeId,
      employee?.showId,
      employee?.code,
      payslip?.empCode,
    ) ?? '-',
  );
  const employeeNameDisplay = resolveFirstString(employee?.name) ?? '-';

  // console.log(employee,"employee");
  const joiningDateDisplay = formatDate(
    employee?.pfDateOfJoining ?? employee?.pfJoinDate ?? employee?.pf?.dateOfJoining ?? null,
  );

  const detailRows = [
    {
      leftLabel: 'Employee ID',
      leftValue: employeeIdDisplay,
      rightLabel: 'Month',
      rightValue: payslip?.month ?? '-',
    },
    {
      leftLabel: 'Employee Name',
      leftValue: employeeNameDisplay,
      rightLabel: 'Date of Joining',
      rightValue: joiningDateDisplay,
    },
    {
      leftLabel: 'Designation',
      leftValue: designationDisplay,
      rightLabel: 'UAN Number',
      rightValue: pfNumberDisplay,
    },
    {
      leftLabel: "Father's Name",
      leftValue: fatherOrHusbandDisplay,
      rightLabel: 'ESI Number',
      rightValue: esiNumberDisplay,
    },
    {
      leftLabel: 'Phone Number',
      leftValue: phoneNumberDisplay,
      rightLabel: 'Location / Branch',
      rightValue: branchDisplayValue,
    },
  ];

  const summaryRows = [
    { label: 'Cal Days', value: payslip?.calDays ?? '-' },
    { label: 'Working Days', value: payslip?.payDays ?? payslip?.pay_days ?? '-' },
    {
      label: 'LOP Days',
      value: payslip?.lopDays ?? payslip?.lop ?? payslip?.lossOfPay ?? '-',
    },
    { label: 'OT Days', value: payslip?.otDays ?? '-' },
  ];

  if (!payslip) {
    return null;
  }

  const handleDownloadPdf = async () => {
    if (!canDownloadPayslipPdf) {
      window.alert('You do not have permission to download payslip PDF.');
      return;
    }

    if (!payslip?.id || isDownloadingPdf) {
      return;
    }

    setIsDownloadingPdf(true);

    try {
      const token = localStorage.getItem('authToken');
      const activeCompanyId = getActiveCompanyId();
      const response = await fetch(`${API_BASE_URL}/api/admin/payslips/${payslip.id}/pdf/modern`, {
        method: 'GET',
        headers: {
          Accept: 'application/pdf',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(activeCompanyId ? { 'x-company-id': activeCompanyId } : {}),
        },
      });

      if (response.status === 401) {
        clearAuthSession(navigate);
        throw new Error('Your session has expired. Please login again.');
      }

      if (!response.ok) {
        const message = await getErrorMessageFromResponse(response, 'Failed to download payslip PDF.');
        throw new Error(message);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const employeeSegment = sanitizeFileSegment(employeeNameDisplay, 'employee');
      const monthSegment = sanitizeFileSegment(payslip?.month, 'month');
      const link = document.createElement('a');
      link.href = url;
      link.download = `payslip-${employeeSegment}-${monthSegment}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60000);
    } catch (error) {
      window.alert(error.message || 'Unable to download payslip PDF.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const netPayInWords = resolvedNetPayAmount != null
    ? amountToWords(resolvedNetPayAmount)
    : '';

  return (
    <DashboardLayout title="Pay Slip">
      <div className="payslip-view">
        <div className="payslip-actions page-actions no-print d-flex justify-content-between align-items-center flex-wrap gap-2">
          <button className="btn btn-outline-secondary no-print" onClick={() => navigate('/payslip/list')}>
            Back to List
          </button>
          <div className="d-flex flex-wrap gap-2">
            {canDownloadPayslipPdf && (
              <button
                type="button"
                className="btn btn-primary no-print"
                onClick={handleDownloadPdf}
                disabled={isDownloadingPdf}
              >
                <FaDownload className="me-2" aria-hidden="true" />
                Download PDF
              </button>
            )}
            <button
              type="button"
              className="btn btn-danger no-print"
              onClick={handlePrint}
            >
              <FaPrint className="me-2" aria-hidden="true" />
              Print Payslip
            </button>
            <button type="button" className="btn btn-outline-success no-print" disabled>
              <FaEnvelope className="me-2" aria-hidden="true" />
              Send to Employee
            </button>
          </div>
        </div>

        <div
          className="payslip-print-area"
          id="payslip-print-area"
        >
          <div className="payslip-sheet">
          <header className="payslip-header">
            <div className="payslip-header-logo">
              {resolvedLogo ? (
                <img src={resolvedLogo} alt={resolvedCompanyName} />
              ) : (
                <div className="payslip-header-logo-fallback" aria-hidden="true">
                  {resolvedCompanyName?.charAt(0) ?? 'C'}
                </div>
              )}
            </div>
            <div className="payslip-header-details">
              <h2 className="payslip-company-name">{resolvedCompanyName}</h2>
              <p className="payslip-company-address">{resolvedCompanyAddress}</p>
              <p className="payslip-company-contact">
                {resolvedCompanyEmail && <span>Email: {resolvedCompanyEmail}</span>}
                {resolvedCompanyEmail && resolvedCompanyPhone && <span className="mx-2">|</span>}
                {resolvedCompanyPhone && <span>Phone: {resolvedCompanyPhone}</span>}
              </p>
            </div>
          </header>

          <div className="payslip-title">Pay Slip</div>

          <section className="payslip-section">
            <table className="payslip-details-table">
              <tbody>
                {detailRows.map((row) => (
                  <tr key={row.leftLabel}>
                    <th scope="row">{row.leftLabel}</th>
                    <td>{row.leftValue ?? '-'}</td>
                    <th scope="row">{row.rightLabel}</th>
                    <td>{row.rightValue ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="payslip-section">
            <table className="payslip-summary-table">
              <tbody>
                {summaryRows.map((item) => (
                  <tr key={item.label}>
                    <th scope="row">{item.label}</th>
                    <td>{item.value ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="payslip-section">
            <div className="row g-4 salary-deduction-grid tables-row">
              <div className="col-md-6">
                <table className="payslip-amounts-table">
                  <thead>
                    <tr>
                      <th colSpan={3}>Salary</th>
                    </tr>
                    <tr>
                      <th>Description</th>
                      <th>Gross</th>
                      <th>Salary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.map((item) => (
                      <tr key={item.label}>
                        <td>{item.label}</td>
                        <td>{formatAmount(item.gross)}</td>
                        <td>{formatAmount(item.earned)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th>Total</th>
                      <td>{formatAmount(totalGrossSummary)}</td>
                      <td>{formatAmount(totalEarnings)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="col-md-6">
                <table className="payslip-amounts-table">
                  <thead>
                    <tr>
                      <th colSpan={2}>Deductions</th>
                    </tr>
                    <tr>
                      <th>Description</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deductions.map((item) => (
                      <tr key={item.label}>
                        <td>{item.label}</td>
                        <td>{formatAmount(item.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th>Total Deductions</th>
                      <td>{formatAmount(totalDeductions)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </section>

          <section className="payslip-summary-line">
            <div>
              <span className="label">Account Number:</span>
              <span className="value">{accountNumberValue ?? '-'}</span>
            </div>
            <div>
              <span className="label">IFSC Code:</span>
              <span className="value">{payslip.ifscCode ?? '-'}</span>
            </div>
            <div>
              <span className="label">Bank Name:</span>
              <span className="value">{payslip.bankName ?? '-'}</span>
            </div>
          </section>

          <section className="payslip-netpay">
            <div className="netpay-label">Net Pay</div>
            <div className="netpay-value">
              {formatAmount(resolvedNetPayAmount)}
            </div>
          </section>
          <section className="payslip-netpay-1">
            <section>
              {netPayInWords && (
                <div className="netpay-words">
                  {netPayInWords} only
                </div>
              )}
            </section>
          </section>

          <footer className="payslip-footer signatures">
            <div className="signature-block">Signature of the Employee</div>
            <div className="signature-block">Manager Signature</div>
          </footer>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PaySlipView;
